import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { COLORS } from './pdfStyles';

// =============================================================================
// STYLES
// =============================================================================
const styles = StyleSheet.create({
  // Page
  page: {
    paddingTop: 0,
    paddingBottom: 60,
    paddingHorizontal: 0,
    fontSize: 10,
    color: COLORS.grisFonce,
    backgroundColor: COLORS.blanc,
  },

  // Header
  header: {
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 20,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    letterSpacing: -0.5, // tracking-tight comme sur la landing page
  },
  headerRight: {
    textAlign: 'right',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 4,
  },
  headerDate: {
    fontSize: 10,
    color: COLORS.dore,
  },
  bandeDore: {
    height: 4,
    backgroundColor: COLORS.dore,
  },

  // Contenu
  content: {
    paddingHorizontal: 40,
    paddingTop: 25,
  },

  // Encart chantier (bleu roi)
  chantierBox: {
    backgroundColor: COLORS.bleuRoi,
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  chantierNom: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 10,
  },
  chantierInfo: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  chantierLabel: {
    fontSize: 10,
    color: COLORS.dore,
    width: 100,
  },
  chantierValue: {
    fontSize: 10,
    color: COLORS.blanc,
    flex: 1,
  },

  // KPI Cards row
  kpiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  kpiCard: {
    flex: 1,
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    marginHorizontal: 5,
    alignItems: 'center',
  },
  kpiCardFirst: {
    marginLeft: 0,
  },
  kpiCardLast: {
    marginRight: 0,
  },
  kpiCardAlert: {
    backgroundColor: COLORS.rougeClair,
  },
  kpiCardWarning: {
    backgroundColor: COLORS.orangeClair,
  },
  kpiCardSuccess: {
    backgroundColor: COLORS.vertClair,
  },
  kpiTitle: {
    fontSize: 9,
    color: COLORS.grisTexte,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  kpiValue: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.noir,
  },
  kpiSubvalue: {
    fontSize: 10,
    marginTop: 2,
    color: COLORS.grisTexte,
  },

  // Separateur dore
  separator: {
    height: 2,
    backgroundColor: COLORS.dore,
    marginVertical: 15,
    opacity: 0.5,
  },

  // Section
  section: {
    marginBottom: 18,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },

  // Tableau
  table: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableHeaderCell: {
    color: COLORS.blanc,
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tableRowAlt: {
    backgroundColor: COLORS.grisClair,
  },
  tableRowTotal: {
    backgroundColor: '#EFF6FF',
    borderBottomWidth: 0,
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
  },
  tableCellRight: {
    flex: 1,
    textAlign: 'right',
    fontSize: 9,
  },
  tableCellBold: {
    fontFamily: 'Helvetica-Bold',
  },

  // Ecarts colores
  ecartPositif: {
    color: COLORS.vertSucces,
    fontFamily: 'Helvetica-Bold',
  },
  ecartNegatif: {
    color: COLORS.rougeAlerte,
    fontFamily: 'Helvetica-Bold',
  },

  // Status box
  statusBox: {
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusLeft: {
    flexDirection: 'column',
  },
  statusLabel: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
  },
  statusMessage: {
    fontSize: 9,
    color: COLORS.blanc,
    opacity: 0.9,
    marginTop: 2,
  },
  statusRight: {
    alignItems: 'flex-end',
  },
  statusValue: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
  },
  statusUnit: {
    fontSize: 10,
    color: COLORS.blanc,
    opacity: 0.9,
  },

  // Recommandations
  recoBox: {
    backgroundColor: COLORS.orangeClair,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.orangeAttention,
  },
  recoBoxCritique: {
    backgroundColor: COLORS.rougeClair,
    borderLeftColor: COLORS.rougeAlerte,
  },
  recoBoxOk: {
    backgroundColor: COLORS.vertClair,
    borderLeftColor: COLORS.vertSucces,
  },
  recoBullet: {
    fontSize: 10,
    marginRight: 8,
  },
  recoText: {
    fontSize: 10,
    color: COLORS.grisFonce,
    flex: 1,
    lineHeight: 1.4,
  },
  recoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  // Point critique
  pointCritiqueBox: {
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  pointCritiqueItem: {
    alignItems: 'center',
  },
  pointCritiqueLabel: {
    fontSize: 9,
    color: COLORS.grisTexte,
    marginBottom: 4,
  },
  pointCritiqueValue: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
  },
  pointCritiqueUnit: {
    fontSize: 9,
    color: COLORS.grisTexte,
  },
  pointCritiqueArrow: {
    fontSize: 20,
    color: COLORS.dore,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  footerBande: {
    height: 3,
    backgroundColor: COLORS.dore,
  },
  footerContent: {
    backgroundColor: COLORS.grisClair,
    paddingVertical: 12,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: COLORS.grisTexte,
  },
  footerPage: {
    fontSize: 8,
    color: COLORS.bleuRoi,
    fontFamily: 'Helvetica-Bold',
  },
});

// =============================================================================
// TYPES
// =============================================================================
export interface RapportRetardData {
  nomChantier: string;
  client?: string;
  budgetHt: number;
  dureePrevue: number;
  dateFinPrevue?: string;
  // Simulation
  joursRetard: number;
  penalitesActivees: boolean;
  typePenalite: 'percentage' | 'fixed' | 'tiered';
  valeurPenalite: number;
  // Resultats
  coutMainOeuvre: number;
  montantPenalites: number;
  coutTotalRetard: number;
  pointCritique: number;
  // Comparaison
  coutsSansRetard: number;
  coutsAvecRetard: number;
  margeSansRetard: number;
  margeAvecRetard: number;
  rentabiliteSansRetard: number;
  rentabiliteAvecRetard: number;
  // Recommandations
  recommandations: string[];
}

interface RapportRetardPDFProps {
  data: RapportRetardData;
}

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================
const formatMontant = (montant: number): string => {
  // Utiliser un formatage manuel pour éviter les caractères spéciaux
  // (les espaces insécables de Intl.NumberFormat peuvent s'afficher comme "/" dans react-pdf)
  const rounded = Math.round(montant);
  const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return formatted + 'EUR';
};

const formatPct = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// Nettoyer les emojis des textes (react-pdf ne les supporte pas)
const cleanEmojis = (text: string): string => {
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '').trim();
};

const getStatusConfig = (rentabilite: number) => {
  if (rentabilite < 0) return {
    label: 'DEFICIT',
    message: 'Le chantier devient deficitaire avec ce retard',
    bgColor: COLORS.rougeAlerte
  };
  if (rentabilite < 5) return {
    label: 'CRITIQUE',
    message: 'Risque de deficit imminent',
    bgColor: '#DC2626'
  };
  if (rentabilite < 10) return {
    label: 'ATTENTION',
    message: 'Marge de securite reduite',
    bgColor: '#F59E0B'
  };
  return {
    label: 'RENTABLE',
    message: 'Le chantier reste rentable',
    bgColor: '#16A34A'
  };
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
export function RapportRetardPDF({ data }: RapportRetardPDFProps) {
  const statusConfig = getStatusConfig(data.rentabiliteAvecRetard);
  const ecartMarge = data.margeAvecRetard - data.margeSansRetard;
  const ecartRentabilite = data.rentabiliteAvecRetard - data.rentabiliteSansRetard;

  const penaliteLabel = data.typePenalite === 'percentage'
    ? `${data.valeurPenalite}% / jour`
    : data.typePenalite === 'fixed'
      ? `${data.valeurPenalite} EUR / jour`
      : 'Paliers CCAG';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerTitle}>SIMULATION DE RETARD</Text>
            <Text style={styles.headerDate}>{format(new Date(), 'd MMMM yyyy', { locale: fr })}</Text>
          </View>
        </View>
        <View style={styles.bandeDore} />

        <View style={styles.content}>
          {/* Encart infos chantier */}
          <View style={styles.chantierBox}>
            <Text style={styles.chantierNom}>{data.nomChantier}</Text>
            <View style={styles.chantierInfo}>
              <Text style={styles.chantierLabel}>Client</Text>
              <Text style={styles.chantierValue}>{data.client || 'Non renseigne'}</Text>
            </View>
            <View style={styles.chantierInfo}>
              <Text style={styles.chantierLabel}>Budget HT</Text>
              <Text style={styles.chantierValue}>{formatMontant(data.budgetHt)}</Text>
            </View>
            <View style={styles.chantierInfo}>
              <Text style={styles.chantierLabel}>Duree prevue</Text>
              <Text style={styles.chantierValue}>{data.dureePrevue} jours</Text>
            </View>
            <View style={styles.chantierInfo}>
              <Text style={styles.chantierLabel}>Retard simule</Text>
              <Text style={styles.chantierValue}>{data.joursRetard} jour{data.joursRetard > 1 ? 's' : ''}</Text>
            </View>
          </View>

          {/* KPIs Impact */}
          <View style={styles.kpiRow}>
            <View style={[styles.kpiCard, styles.kpiCardFirst, styles.kpiCardWarning]}>
              <Text style={styles.kpiTitle}>Main d'oeuvre supp.</Text>
              <Text style={[styles.kpiValue, { color: COLORS.orangeAttention }]}>
                {formatMontant(data.coutMainOeuvre)}
              </Text>
            </View>

            <View style={[styles.kpiCard, data.penalitesActivees ? styles.kpiCardAlert : {}]}>
              <Text style={styles.kpiTitle}>Penalites contrat</Text>
              <Text style={[styles.kpiValue, { color: data.penalitesActivees ? COLORS.rougeAlerte : COLORS.grisTexte }]}>
                {formatMontant(data.montantPenalites)}
              </Text>
              <Text style={styles.kpiSubvalue}>
                {data.penalitesActivees ? penaliteLabel : 'Desactivees'}
              </Text>
            </View>

            <View style={[styles.kpiCard, styles.kpiCardLast, styles.kpiCardAlert]}>
              <Text style={styles.kpiTitle}>Cout total retard</Text>
              <Text style={[styles.kpiValue, { color: COLORS.rougeAlerte }]}>
                {formatMontant(data.coutTotalRetard)}
              </Text>
            </View>
          </View>

          <View style={styles.separator} />

          {/* Statut global */}
          <View style={[styles.statusBox, { backgroundColor: statusConfig.bgColor }]}>
            <View style={styles.statusLeft}>
              <Text style={styles.statusLabel}>{statusConfig.label}</Text>
              <Text style={styles.statusMessage}>{statusConfig.message}</Text>
            </View>
            <View style={styles.statusRight}>
              <Text style={styles.statusValue}>{formatPct(data.rentabiliteAvecRetard)}</Text>
              <Text style={styles.statusUnit}>rentabilite simulee</Text>
            </View>
          </View>

          <View style={styles.separator} />

          {/* Comparaison Sans/Avec Retard */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>COMPARAISON SANS / AVEC RETARD</Text>
            </View>

            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Indicateur</Text>
                <Text style={[styles.tableHeaderCell, styles.tableCellRight]}>Sans Retard</Text>
                <Text style={[styles.tableHeaderCell, styles.tableCellRight]}>Avec Retard</Text>
                <Text style={[styles.tableHeaderCell, styles.tableCellRight]}>Ecart</Text>
              </View>

              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2 }]}>Couts totaux</Text>
                <Text style={styles.tableCellRight}>{formatMontant(data.coutsSansRetard)}</Text>
                <Text style={styles.tableCellRight}>{formatMontant(data.coutsAvecRetard)}</Text>
                <Text style={[styles.tableCellRight, styles.ecartNegatif]}>
                  +{formatMontant(data.coutTotalRetard)}
                </Text>
              </View>

              <View style={[styles.tableRow, styles.tableRowAlt]}>
                <Text style={[styles.tableCell, { flex: 2 }]}>Marge finale</Text>
                <Text style={styles.tableCellRight}>{formatMontant(data.margeSansRetard)}</Text>
                <Text style={styles.tableCellRight}>{formatMontant(data.margeAvecRetard)}</Text>
                <Text style={[styles.tableCellRight, ecartMarge >= 0 ? styles.ecartPositif : styles.ecartNegatif]}>
                  {ecartMarge >= 0 ? '+' : ''}{formatMontant(ecartMarge)}
                </Text>
              </View>

              <View style={[styles.tableRow, styles.tableRowTotal]}>
                <Text style={[styles.tableCell, styles.tableCellBold, { flex: 2 }]}>Rentabilite</Text>
                <Text style={[styles.tableCellRight, styles.tableCellBold]}>{formatPct(data.rentabiliteSansRetard)}</Text>
                <Text style={[styles.tableCellRight, styles.tableCellBold]}>{formatPct(data.rentabiliteAvecRetard)}</Text>
                <Text style={[styles.tableCellRight, styles.tableCellBold, ecartRentabilite >= 0 ? styles.ecartPositif : styles.ecartNegatif]}>
                  {ecartRentabilite >= 0 ? '+' : ''}{formatPct(ecartRentabilite)}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.separator} />

          {/* Point Critique */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>POINT CRITIQUE</Text>
            </View>

            <View style={styles.pointCritiqueBox}>
              <View style={styles.pointCritiqueItem}>
                <Text style={styles.pointCritiqueLabel}>Retard simule</Text>
                <Text style={[styles.pointCritiqueValue, { color: COLORS.bleuRoi }]}>
                  {data.joursRetard}
                </Text>
                <Text style={styles.pointCritiqueUnit}>jours</Text>
              </View>

              <Text style={styles.pointCritiqueArrow}>--{'>'}</Text>

              <View style={styles.pointCritiqueItem}>
                <Text style={styles.pointCritiqueLabel}>Point critique</Text>
                <Text style={[styles.pointCritiqueValue, {
                  color: data.joursRetard >= data.pointCritique ? COLORS.rougeAlerte : COLORS.vertSucces
                }]}>
                  {data.pointCritique < 100 ? data.pointCritique : '> 100'}
                </Text>
                <Text style={styles.pointCritiqueUnit}>jours max</Text>
              </View>

              <Text style={styles.pointCritiqueArrow}>--{'>'}</Text>

              <View style={styles.pointCritiqueItem}>
                <Text style={styles.pointCritiqueLabel}>Marge restante</Text>
                <Text style={[styles.pointCritiqueValue, {
                  color: data.pointCritique - data.joursRetard > 5 ? COLORS.vertSucces :
                         data.pointCritique - data.joursRetard > 0 ? COLORS.orangeAttention : COLORS.rougeAlerte
                }]}>
                  {data.pointCritique < 100 ? Math.max(0, data.pointCritique - data.joursRetard) : '> 100'}
                </Text>
                <Text style={styles.pointCritiqueUnit}>jours</Text>
              </View>
            </View>
          </View>

          <View style={styles.separator} />

          {/* Recommandations */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>RECOMMANDATIONS</Text>
            </View>

            {data.recommandations.map((reco, i) => {
              const cleanReco = cleanEmojis(reco);
              const isOk = cleanReco.includes('rentable') || cleanReco.includes('Continuer');
              const isCritique = cleanReco.includes('URGENT') || cleanReco.includes('ALERTE') || cleanReco.includes('critique');
              return (
                <View key={i} style={[
                  styles.recoBox,
                  isOk && styles.recoBoxOk,
                  isCritique && styles.recoBoxCritique,
                ]}>
                  <View style={styles.recoRow}>
                    <Text style={styles.recoBullet}>*</Text>
                    <Text style={styles.recoText}>{cleanReco}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View style={styles.footerBande} />
          <View style={styles.footerContent}>
            <Text style={styles.footerText}>
              B8ild - Rapport de simulation de retard genere le {format(new Date(), 'd MMMM yyyy', { locale: fr })}
            </Text>
            <Text style={styles.footerPage}>Page 1/1</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
